import React, { useState, useEffect, useContext } from 'react'
import Overview from './components/Overview'
import CalendarSelector from './components/CalendarSelector'
import DateSelector from './components/DateSelector'
import TimeSlotSelector from './components/TimeSlotSelector'
import ConfirmationPage from './components/ConfirmationPage'
import DetailsForm from './components/DetailsForm'
import { WidgetContext } from '../Widget2'
import { format } from 'date-fns'
import extractErrors from '../../helpers/extractErrors'
import buildAvailableLocationsFromUserAppointmentCalendar from './util/buildAvailableLocationsFromUserAppointmentCalendar'
import './css/layout.scss'
import './css/style.scss'
import './css/calendar.scss'

const defaultMeetingDetails = {
  guestName: '',
  guestEmail: '',
  guestPhone: '',
  additionalGuests: '',
  message: '',
  location: ''
}

const AppointmentScheduler = ({ userId, userAppointmentCalendarId, iframe }) => {
  const { client, host, franchise, setUnrecoverableError } = useContext(WidgetContext)
  const { dateFormat } = franchise.attributes

  const [userAppointmentCalendars, setUserAppointmentCalendars] = useState([])
  const [userAppointmentCalendar, setUserAppointmentCalendar] = useState(null)
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [selectedTime, setSelectedTime] = useState(null)
  const [appointmentSuccessfullyScheduled, setAppointmentSuccessfullyScheduled] = useState(false)
  const [slots, setSlots] = useState([])
  const [meetingDetails, setMeetingDetails] = useState(defaultMeetingDetails)
  const [meetingDetailErrors, setMeetingDetailErrors] = useState({})
  const availableLocations = buildAvailableLocationsFromUserAppointmentCalendar(userAppointmentCalendar)

  useEffect(() => {
    if(userAppointmentCalendarId) {
      loadSingleUserAppointmentCalendar()
    }
    else {
      loadAllUserAppointmentCalendars()
    }
  }, [])

  useEffect(() => {
    if(selectedDate && userAppointmentCalendar) {
      loadSlots()
    }
    else {
      setSlots([])
    }
  }, [selectedDate, userAppointmentCalendar])

  async function loadAllUserAppointmentCalendars() {
    try {
      const response = await client.get(`${host}/api/v1/users/${userId}/user_appointment_calendars`)
      if(response.data.data.length == 1) {
        // If there is only one calendar, select it automatically
        setUserAppointmentCalendar(response.data.data[0])
      }
      else {
        setUserAppointmentCalendars(response.data.data)
      }
    }
    catch (err) {
      console.error(err)
      setUnrecoverableError("An error occurred while loading user appointment calendar. Please refresh the page and try again.")
    }
  } 

  async function loadSingleUserAppointmentCalendar() {
    try {
      const response = await client.get(`${host}/api/v1/users/${userId}/user_appointment_calendars/${userAppointmentCalendarId}`)
      setUserAppointmentCalendar(response.data.data)
    }
    catch (err) {
      console.error(err)
      setUnrecoverableError("An error occurred while loading user appointment calendar. Please refresh the page and try again.")
    }
  } 

  async function loadSlots() {
    setSlotsLoading(true)
    try {
      const params = {
        selected_date: format(selectedDate, dateFormat),
        user_id: userId,
        user_appointment_calendar_id: userAppointmentCalendar.id
      }
      const response = await  client.get(`${host}/api/v1/available_appointment_slots`, { params })
      const slots = response.data.data
      setSlots(slots)
      setSlotsLoading(false)
    }
    catch (err) {
      console.error(err)
      setUnrecoverableError("An error occurred while loading available appointment slots. Please refresh the page and try again.")
      setSlotsLoading(false)
    }
  }

  async function createAppointment() {
    try {
      const formattedStartDate = format(selectedDate, "yyyy-MM-dd")

      const formBody = {
        ...meetingDetails,
        userId,
        startsAtDate: formattedStartDate,
        startsAtTime: selectedTime,
        createdVia: "widget",
        userAppointmentCalendarId: userAppointmentCalendar.id
      }
      const response = await client.post(`${host}/api/v1/appointments`, formBody)
      setAppointmentSuccessfullyScheduled(true)
      setMeetingDetailErrors({})
    }
    catch (err) {
      const errors = extractErrors(err)
      console.warn({ errors })
      setMeetingDetailErrors(errors)
    }
  }

  return (
    <div className={`wrapper ${iframe ? "within-iframe" : ""}`}>
      <div className="appointment-scheduler-widget">
        <div className={`appointment-scheduler-content`}>
          <Overview
            userAppointmentCalendar={userAppointmentCalendar}
            userAppointmentCalendars={userAppointmentCalendars}
            onBack={() => {
              setUserAppointmentCalendar(null)
              setSelectedDate(null)
              setSelectedTime(null)
            }}
          />

          { !userAppointmentCalendar &&
            <CalendarSelector
              userAppointmentCalendars={userAppointmentCalendars}
              onCalendarSelect={(userAppointmentCalendar) => {
                setUserAppointmentCalendar(userAppointmentCalendar)
                setSelectedDate(new Date())
              }}
            />
          }

          { (userAppointmentCalendar && !appointmentSuccessfullyScheduled && (!selectedDate || !selectedTime)) &&
            <DateSelector
              dateFormat={dateFormat}
              selectedDate={selectedDate}
              setSelectedDate={date => {
                setSelectedDate(date)
                setSelectedTime(null)
              }}
            />
          }

          {(userAppointmentCalendar && !appointmentSuccessfullyScheduled && selectedDate && !selectedTime ) &&
            <TimeSlotSelector
              selectedDate={selectedDate}
              setSelectedTime={setSelectedTime}
              slots={slots}
              slotsLoading={slotsLoading}
            />
          }

          { (userAppointmentCalendar && !appointmentSuccessfullyScheduled && selectedDate && selectedTime) && (
            <DetailsForm
              availableLocations={availableLocations}
              dateFormat={dateFormat}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              meetingDetails={meetingDetails}
              setMeetingDetails={setMeetingDetails}
              meetingDetailErrors={meetingDetailErrors}
              userAppointmentCalendar={userAppointmentCalendar}
              onBack={() => {
                setSelectedTime(null)
              }}
              onSubmit={createAppointment}
            />
          )}

          { userAppointmentCalendar && appointmentSuccessfullyScheduled &&
            <ConfirmationPage
              userAppointmentCalendar={userAppointmentCalendar}
              dateFormat={dateFormat}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              meetingDetails={meetingDetails}
            />
          }
        </div>
      </div>
    </div>
  )
}

export default AppointmentScheduler
